<script setup lang="ts">
import { onMounted, computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { useHistoryStore } from '@/stores/history';
import { useCreateStore } from '@/stores/create';
import { MODES } from '@/lib/modes';
import { templatesByCategory, findTemplate, TEMPLATES } from '@/lib/templates';
import type { Template } from '@/lib/templates';
import { getRecentTemplateIds } from '@/lib/recent-templates';
import MxIconShelf from '@/components/MxIconShelf.vue';
import MxTemplateCard from '@/components/MxTemplateCard.vue';
import MxTemplateModal from '@/components/MxTemplateModal.vue';
import MxIcon from '@/components/MxIcon.vue';
import MxSkeleton from '@/components/MxSkeleton.vue';
import MxVideoPreview from '@/components/MxVideoPreview.vue';

const router = useRouter();
const userStore = useUserStore();
const historyStore = useHistoryStore();
const createStore = useCreateStore();
const { me } = storeToRefs(userStore);
const { items, loading } = storeToRefs(historyStore);
const { models } = storeToRefs(createStore);

const greeting = computed(() => {
  const name = me.value?.user.first_name ?? '';
  return name ? `Привет, ${name}` : 'Привет';
});

const recent = computed(() => items.value.slice(0, 6));

const modeItems = MODES.map((m) => ({ id: m.kind, icon: m.icon, label: m.label }));

const MODEL_EMOJI: Record<string, string> = {
  seedance_2: '🚀',
  seedance_2_fast: '⚡',
  grok_imagine: '✨',
  wan_2_6_flash: '🌊',
  veo3_fast: '🎬',
  veo3: '🎥',
  kling_3_motion: '🎭',
  gpt_image_2: '🖼',
  nano_banana_2: '🍌',
};

const videoModelItems = computed(() =>
  models.value
    .filter((m) => m.kind === 'video')
    .map((m) => ({
      id: m.key,
      emoji: MODEL_EMOJI[m.key] ?? '🎬',
      label: m.label,
    })),
);

const trends = templatesByCategory('trends');
const classic = templatesByCategory('classic');

const recentTemplates = ref<Template[]>([]);
function refreshRecent() {
  recentTemplates.value = getRecentTemplateIds()
    .map((id) => findTemplate(id))
    .filter((t): t is Template => t !== null)
    .slice(0, 6);
}

const selected = ref<Template | null>(null);
const modalOpen = ref(false);

function pickMode(id: string) { router.push(`/create/${id}`); }
function pickModel(id: string) { router.push(`/create/video?model=${encodeURIComponent(id)}`); }
function openTemplate(t: Template) { selected.value = t; modalOpen.value = true; }
function goTemplate(t: Template) {
  modalOpen.value = false;
  router.push(`/create/${t.kind}?template=${t.id}`);
}

function surpriseMe() {
  if (TEMPLATES.length === 0) return;
  const last = recentTemplates.value[0]?.id;
  const pool = TEMPLATES.filter((t) => t.id !== last);
  const arr = pool.length > 0 ? pool : TEMPLATES;
  const t = arr[Math.floor(Math.random() * arr.length)];
  router.push(`/create/${t.kind}?template=${t.id}`);
}

onMounted(async () => {
  refreshRecent();
  await createStore.loadModels('video');
  await historyStore.loadFirst('all');
});
</script>

<template>
  <section class="home">
    <h1 class="greet">{{ greeting }}</h1>

    <h3 class="sect">Создать</h3>
    <MxIconShelf
      :items="modeItems"
      size="lg"
      @update:active-id="pickMode"
    />

    <h3 class="sect">Модели</h3>
    <MxIconShelf
      :items="videoModelItems"
      @update:active-id="pickModel"
    />

    <template v-if="recentTemplates.length">
      <h3 class="sect">Недавние</h3>
      <div class="hcards">
        <MxTemplateCard
          v-for="t in recentTemplates"
          :key="t.id"
          :template="t"
          @select="openTemplate"
        />
      </div>
    </template>

    <div v-if="trends.length" class="row">
      <h3 class="sect sect--row">Тренды</h3>
      <div class="row__actions">
        <button class="surprise" data-test="surprise-me" @click="surpriseMe">
          <MxIcon name="Shuffle" :size="14" />
          <span>Удиви меня</span>
        </button>
        <RouterLink to="/ideas?cat=trends" class="more">Все →</RouterLink>
      </div>
    </div>
    <div v-if="trends.length" class="hcards">
      <MxTemplateCard
        v-for="t in trends"
        :key="t.id"
        :template="t"
        @select="openTemplate"
      />
    </div>

    <div v-if="classic.length" class="row">
      <h3 class="sect sect--row">Классика</h3>
      <RouterLink to="/ideas?cat=classic" class="more">Все →</RouterLink>
    </div>
    <div v-if="classic.length" class="hcards">
      <MxTemplateCard
        v-for="t in classic"
        :key="t.id"
        :template="t"
        @select="openTemplate"
      />
    </div>

    <h3 class="sect">Последние работы</h3>
    <div v-if="loading && recent.length === 0" class="rec__row">
      <MxSkeleton v-for="n in 3" :key="n" height="140px" radius="var(--r-lg)" />
    </div>
    <div v-else-if="recent.length === 0" class="empty">
      <div class="empty__frame"><MxIcon name="Film" :size="28" /></div>
      <p>Здесь появятся ваши работы.</p>
      <RouterLink to="/create/video" class="empty__cta">Создать первое →</RouterLink>
    </div>
    <div v-else class="rec__row">
      <RouterLink
        v-for="g in recent"
        :key="g.id"
        :to="`/generation/${g.id}`"
        class="rec__cell"
      >
        <MxVideoPreview :src="g.result_url" :poster="g.preview_url" :kind="g.type" aspect="9 / 16" />
      </RouterLink>
    </div>

    <MxTemplateModal v-model:open="modalOpen" :template="selected" @select="goTemplate" />
  </section>
</template>

<style scoped>
.home { padding: var(--sp-2) 0 calc(96px + env(safe-area-inset-bottom)); }
.greet {
  font-family: var(--font-display);
  font-size: var(--fs-2xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-bottom: var(--sp-5);
}
.sect {
  font-size: var(--fs-sm);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
  margin: var(--sp-5) 0 var(--sp-3);
}
.sect--row { margin: 0; }
.row { display: flex; align-items: center; justify-content: space-between; margin: var(--sp-5) 0 var(--sp-3); gap: var(--sp-3); }
.row__actions { display: inline-flex; align-items: center; gap: var(--sp-3); }
.more { color: var(--accent); font-size: var(--fs-sm); font-weight: 500; white-space: nowrap; }
.surprise {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: var(--fs-sm);
  font-weight: 600;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease;
}
.surprise:active { transform: scale(0.96); }
.hcards {
  display: flex;
  gap: var(--sp-3);
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 4px;
}
.hcards::-webkit-scrollbar { display: none; }
.hcards > * {
  flex: 0 0 60%;
  max-width: 220px;
  scroll-snap-align: start;
}
.rec__row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-3);
}
.rec__cell {
  display: block;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease;
}
.rec__cell:active { transform: scale(0.97); }
.empty {
  display: flex; flex-direction: column; align-items: flex-start; gap: var(--sp-3);
  padding: var(--sp-6) var(--sp-2); color: var(--text-secondary);
}
.empty__frame {
  width: 100%; aspect-ratio: 16 / 7;
  border-radius: var(--r-lg);
  border: 1px dashed var(--hairline);
  background: var(--bg-elevated);
  display: grid; place-items: center;
  color: var(--text-tertiary);
}
.empty__cta { color: var(--accent); font-weight: 600; }
</style>
